<script lang="ts">
  import { t } from '../i18n/index.svelte';
  import { decodeImage } from '../image';
  import { assets, studio } from '../state.svelte';
  import { defaultTexture, type TextureKind } from '../types';
  import FileButton from './ui/FileButton.svelte';
  import Palette from './ui/Palette.svelte';
  import Section from './ui/Section.svelte';
  import Select from './ui/Select.svelte';
  import TexturePresets from './TexturePresets.svelte';
  import Slider from './ui/Slider.svelte';

  let { allowSource = false }: { allowSource?: boolean } = $props();

  const kinds = $derived(
    (
      [
        ['random_dots', t('texture.kind.random_dots')],
        ['noise', t('texture.kind.noise')],
        ['worley', t('texture.kind.worley')],
        ['stripes', t('texture.kind.stripes')],
        ['marble', t('texture.kind.marble')],
        ['camo', t('texture.kind.camo')],
        ['mosaic', t('texture.kind.mosaic')],
        ['bubbles', t('texture.kind.bubbles')],
        ['plasma', t('texture.kind.plasma')],
        ['rings', t('texture.kind.rings')],
        ['confetti', t('texture.kind.confetti')],
        ['stars', t('texture.kind.stars')],
        ['image', t('texture.kind.image')],
        ...(allowSource ? [['source', t('texture.kind.source')]] : []),
      ] as [TextureKind, string][]
    ).map(([value, label]) => ({ value, label })),
  );

  // The kind follows the texture (presets replace the whole spec).
  const kind = $derived(studio.texture.kind);
  function setKind(k: TextureKind) {
    if (k !== studio.texture.kind) studio.texture = defaultTexture(k);
  }
  $effect(() => {
    // The source texture disappears when the source has no image.
    if (!allowSource && studio.texture.kind === 'source') studio.texture = defaultTexture('random_dots');
  });
  let showPresets = $state(true);

  async function onTexture(f: File) {
    assets.textureImage = await decodeImage(f, 1024);
  }

  const tex = $derived(studio.texture);
</script>

<Section title={t('texture.title')}>
  <div class="row presets-head">
    <span class="small">{t('texture.presets')}</span>
    <button class="ghost tiny" onclick={() => (showPresets = !showPresets)}>{showPresets ? '▾' : '▸'}</button>
  </div>
  {#if showPresets}
    <TexturePresets current={studio.texture} onpick={(spec) => (studio.texture = spec)} />
  {/if}
  <label class="field">
    <span>{t('texture.kind')}</span>
    <select
      value={kind}
      onchange={(e) => setKind((e.currentTarget as HTMLSelectElement).value as TextureKind)}
      data-testid="texture-kind"
    >
      {#each kinds as o (o.value)}<option value={o.value}>{o.label}</option>{/each}
    </select>
  </label>

  {#if tex.kind === 'random_dots'}
    <div class="seg">
      {#each ['bw', 'color', 'palette'] as const as s (s)}
        <button class:on={tex.style === s} onclick={() => (tex.style = s)}>{t(`texture.style.${s}`)}</button>
      {/each}
    </div>
    {#if tex.style !== 'color'}
      <Slider
        label={t('texture.density')}
        bind:value={tex.density}
        min={0.05}
        max={0.95}
        step={0.01}
        format={(v) => v.toFixed(2)}
      />
    {/if}
    <Slider label={t('texture.dotSize')} bind:value={tex.dot_size} min={1} max={6} />
    {#if tex.style === 'palette'}<Palette bind:colors={tex.palette} />{/if}
  {:else if tex.kind === 'noise'}
    <Slider label={t('texture.scale')} bind:value={tex.scale} min={4} max={80} />
    <Slider label={t('texture.octaves')} bind:value={tex.octaves} min={1} max={8} />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'worley'}
    <Slider label={t('texture.scale')} bind:value={tex.scale} min={4} max={80} />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'stripes'}
    <Slider label={t('texture.stripeWidth')} bind:value={tex.stripe_width} min={1} max={30} step={0.5} />
    <Slider
      label={t('texture.waviness')}
      bind:value={tex.waviness}
      min={0}
      max={2}
      step={0.05}
      format={(v) => v.toFixed(2)}
    />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'marble'}
    <Slider label={t('texture.scale')} bind:value={tex.scale} min={4} max={80} />
    <Slider
      label={t('texture.turbulence')}
      bind:value={tex.turbulence}
      min={0}
      max={6}
      step={0.1}
      format={(v) => v.toFixed(1)}
    />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'camo'}
    <Slider label={t('texture.scale')} bind:value={tex.scale} min={4} max={80} />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'mosaic'}
    <Slider label={t('texture.scale')} bind:value={tex.scale} min={4} max={60} />
    <Slider label={t('texture.edge')} bind:value={tex.edge} min={0} max={5} step={0.5} />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'bubbles'}
    <Slider label={t('texture.radius')} bind:value={tex.radius} min={2} max={24} step={0.5} />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'plasma'}
    <Slider label={t('texture.scale')} bind:value={tex.scale} min={4} max={80} />
    <Slider
      label={t('texture.bands')}
      bind:value={tex.bands}
      min={0.3}
      max={5}
      step={0.1}
      format={(v) => v.toFixed(1)}
    />
  {:else if tex.kind === 'rings'}
    <Slider label={t('texture.scale')} bind:value={tex.scale} min={4} max={80} />
    <Slider label={t('texture.rings')} bind:value={tex.rings} min={2} max={40} />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'confetti'}
    <Slider label={t('texture.pieceSize')} bind:value={tex.size} min={2} max={24} step={0.5} />
    <Slider
      label={t('texture.density')}
      bind:value={tex.density}
      min={0.1}
      max={1.5}
      step={0.05}
      format={(v) => v.toFixed(2)}
    />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'stars'}
    <Slider
      label={t('texture.density')}
      bind:value={tex.density}
      min={0.02}
      max={1}
      step={0.01}
      format={(v) => v.toFixed(2)}
    />
    <Palette bind:colors={tex.palette} />
  {:else if tex.kind === 'image'}
    <FileButton label={t('texture.upload')} accept="image/*" onfile={onTexture} testid="texture-input" />
    {#if !assets.textureImage}<p class="muted small">{t('texture.needImage')}</p>{/if}
    <Select
      label={t('texture.seamless')}
      bind:value={tex.seamless}
      options={[
        { value: 'blend', label: t('texture.seamless.blend') },
        { value: 'mirror', label: t('texture.seamless.mirror') },
        { value: 'none', label: t('texture.seamless.none') },
      ]}
    />
  {:else if tex.kind === 'source'}
    <p class="muted small">{t('texture.sourceHint')}</p>
    <Slider
      label={t('texture.tint')}
      bind:value={tex.strength}
      min={0}
      max={1}
      step={0.05}
      format={(v) => `${Math.round(v * 100)}%`}
    />
  {/if}
</Section>

<style>
  .field {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 13px;
  }
  .presets-head {
    justify-content: space-between;
  }
  .tiny {
    padding: 0 6px;
    font-size: 12px;
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
</style>
